import { useEffect, useMemo, useRef, useState } from 'react';

import { useAtom } from 'jotai';

import { logger } from '@/utils/logger/logger';

import { webinarTimeslotAtom, type WebinarTimeslotValue } from '@/atoms/demoAtoms';
import {
  MeetingBooker,
  type MeetingBookerHandle,
} from '@/container/demo-flow/meeting-booker';
import { useQueryWebinarTimeslots } from '@/hooks/query/useQueryWebinarTimeslots';
import { type DemoExperienceVariant } from '@/models/analytics/features/onboarding-hubspot';
import { type OnboardingExperience } from '@/utils/onboardingExperience';
import { Button } from '@heidi/design-system/button';
import { TypographyH2, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';

import { isRequestDemoBlocked } from '@/features/onboarding-v2/utils/isRequestDemoBlocked';

import { useCompletionStepAnalytics } from '@/models/analytics/features/useCompletionStepAnalytics';
import { useResolvedOnboardingExperience } from './hooks/useResolvedOnboardingExperience';
import { MobileCompletionIllustration } from './MobileCompletionIllustration';
import { RequestDemoSection } from './RequestDemoSection';
import { WebinarOptInSection } from './WebinarOptInSection';

interface CompletionStepProps {
  firstName: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  countryName?: string;
  countryCode?: string;
  displayLanguage?: string | null;
  ctaOptIn: boolean;
  onCtaOptInChange: (value: boolean) => void;
  demoReason: string;
  onDemoReasonChange: (value: string) => void;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  onGetStarted: () => void;
  isSubmitting?: boolean;
  error?: string;
}

// Legacy analytics-enum shim — retired alongside legacy events in cleanup PR.
const LEGACY_VARIANT: Record<OnboardingExperience['type'], DemoExperienceVariant> = {
  demo: 'meeting',
  'request-demo': 'sdr-demo',
  webinar: 'webinar',
  hidden: 'none',
};
const toLegacyVariant = (e: OnboardingExperience): DemoExperienceVariant =>
  LEGACY_VARIANT[e.type];

export function CompletionStep({
  firstName,
  lastName,
  email,
  companyName,
  countryName,
  countryCode,
  displayLanguage,
  ctaOptIn,
  onCtaOptInChange,
  demoReason,
  onDemoReasonChange,
  phoneNumber,
  onPhoneNumberChange,
  onGetStarted,
  isSubmitting = false,
  error,
}: CompletionStepProps) {
  const { t } = useTranslations();
  const [webinarTimeslot, setWebinarTimeslot] = useAtom(webinarTimeslotAtom);
  const meetingBookerRef = useRef<MeetingBookerHandle>(null);
  const [isBooking, setIsBooking] = useState(false);

  const { experience, isProbing } = useResolvedOnboardingExperience({
    countryCode,
    countryName,
    languageCode: displayLanguage,
  });
  const webinarRegion = experience.type === 'webinar' ? experience.region : undefined;

  const analytics = useCompletionStepAnalytics({
    demoExperienceVariant: toLegacyVariant(experience),
    webinarRegion,
    country: countryName,
    countryCode,
    language: displayLanguage ?? undefined,
  });

  const { data: webinarTimeslots, isLoading: isLoadingTimeslots } =
    useQueryWebinarTimeslots(
      experience.type === 'webinar' && ctaOptIn ? webinarRegion : undefined
    );

  // Clear stale slot when country/language changes the resolved experience —
  // webinarTimeslotAtom survives those changes and would otherwise leak.
  useEffect(() => {
    if (experience.type !== 'webinar') {
      if (webinarTimeslot) setWebinarTimeslot(undefined);
      return;
    }
    if (
      webinarTimeslot &&
      !(webinarTimeslots ?? []).some((slot) => slot.value === webinarTimeslot.value)
    ) {
      setWebinarTimeslot(undefined);
    }
  }, [experience.type, webinarTimeslot, webinarTimeslots, setWebinarTimeslot]);

  // Mirror the timeslot cleanup for demoReason text. The webinar variant now
  // also collects a reason (optional demo-lead capture), so only clear it when
  // the resolved experience uses neither the request-demo nor webinar field.
  useEffect(() => {
    if (
      experience.type !== 'request-demo' &&
      experience.type !== 'webinar' &&
      demoReason
    ) {
      onDemoReasonChange('');
    }
  }, [experience.type, demoReason, onDemoReasonChange]);

  const webinarTimeslotItems = useMemo(
    () =>
      (webinarTimeslots ?? []).map((slot) => ({
        value: slot.value,
        label: slot.label,
      })),
    [webinarTimeslots]
  );

  const handleWebinarTimeslotSelect = (value: string | undefined) => {
    if (!value) {
      setWebinarTimeslot(undefined);
      return;
    }
    const selectedSlot = webinarTimeslots?.find((slot) => slot.value === value);
    if (selectedSlot) {
      setWebinarTimeslot({
        value: selectedSlot.value,
        label: selectedSlot.label,
        hubspotProperty: selectedSlot.hubspotProperty,
      } as WebinarTimeslotValue);
      analytics.trackTimeslotSelected(selectedSlot.label, selectedSlot.hubspotProperty);
    }
  };

  const capitalizedName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : t('signup.defaultName');

  // HubSpot SDR form requires phone + reason; submission 400s without them.
  const requestDemoBlocked = isRequestDemoBlocked(experience, {
    ctaOptIn,
    demoReason,
    phoneNumber,
  });

  const handleGetStarted = async () => {
    if (isBooking) return;
    setIsBooking(true);
    try {
      const booked = await meetingBookerRef.current?.book();
      if (booked === false) {
        logger.warn('Meeting booking was skipped or no slot selected');
      }
    } catch (err) {
      logger.error('Failed to book meeting during onboarding:', err);
    } finally {
      setIsBooking(false);
    }
    onGetStarted();
  };

  const handleDemoOptInChange = (checked: boolean) => {
    const isOptedIn = checked === true;
    onCtaOptInChange(isOptedIn);
    if (!isOptedIn) {
      setWebinarTimeslot(undefined);
      if (experience.type === 'request-demo' || experience.type === 'webinar') {
        onDemoReasonChange('');
      }
    }
    analytics.trackOptInChanged(isOptedIn);
  };

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TypographyH2 className="text-balance text-center lg:text-left">
          {t('signup.completionTitle', { name: capitalizedName })}
        </TypographyH2>
        <TypographyP className="text-pretty text-center text-text-primary lg:hidden">
          {t('signup.completionSubtitleMobile')}
        </TypographyP>
        <TypographyP className="hidden text-pretty text-text-primary lg:block lg:text-left">
          {t('signup.completionSubtitle')}
        </TypographyP>
      </div>

      <MobileCompletionIllustration />

      {experience.type === 'demo' && !isProbing && (
        <div className="w-full max-w-[400px]">
          <MeetingBooker
            ref={meetingBookerRef}
            meetingUrl={experience.meetingUrl}
            firstName={firstName}
            lastName={lastName}
            email={email}
            companyName={companyName}
          />
        </div>
      )}

      {experience.type === 'webinar' && (
        <WebinarOptInSection
          ctaOptIn={ctaOptIn}
          selectedTimeslotValue={webinarTimeslot?.value}
          timeslotItems={webinarTimeslotItems}
          isLoadingTimeslots={isLoadingTimeslots}
          demoReason={demoReason}
          phoneNumber={phoneNumber}
          defaultCountry={countryCode as never}
          countryName={countryName}
          language={displayLanguage ?? undefined}
          onCtaOptInChange={handleDemoOptInChange}
          onTimeslotSelect={handleWebinarTimeslotSelect}
          onDemoReasonChange={onDemoReasonChange}
          onPhoneNumberChange={onPhoneNumberChange}
        />
      )}

      {experience.type === 'request-demo' && (
        <RequestDemoSection
          ctaOptIn={ctaOptIn}
          demoReason={demoReason}
          phoneNumber={phoneNumber}
          defaultCountry={countryCode as never}
          countryName={countryName}
          language={displayLanguage ?? undefined}
          onCtaOptInChange={handleDemoOptInChange}
          onDemoReasonChange={onDemoReasonChange}
          onPhoneNumberChange={onPhoneNumberChange}
        />
      )}

      {error && (
        <TypographyP className="text-validation-destructive">{error}</TypographyP>
      )}

      <div className="flex flex-1 items-end justify-end pt-4">
        <Button
          type="button"
          size="lg"
          className="w-full lg:w-auto"
          onClick={() => void handleGetStarted()}
          disabled={isSubmitting || isBooking || isProbing || requestDemoBlocked}
        >
          {t('signup.getStarted')}
        </Button>
      </div>
    </div>
  );
}
